<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">{{ pageTitle }}</h1>
      <button class="btn btn-secondary" @click="handleBack">
        <i class="ri-arrow-left-line"></i>
        返回
      </button>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchLogs">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>操作時間</th>
            <th>操作類型</th>
            <th>操作人員</th>
            <th>變更內容</th>
            <th>備註</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="log in logs" :key="log.id">
            <td>{{ formatDateTime(log.changedAt) }}</td>
            <td>
              <span :class="getActionBadgeClass(log.action)">
                {{ getActionText(log.action) }}
              </span>
            </td>
            <td>{{ getChangedByDisplay(log) }}</td>
            <td>
              <div class="change-details">
                <div v-if="log.oldValue" class="old-value">
                  <strong>變更前：</strong>
                  <pre>{{ formatChangeValue(log.oldValue) }}</pre>
                </div>
                <div v-if="log.newValue" class="new-value">
                  <strong>變更後：</strong>
                  <pre>{{ formatChangeValue(log.newValue) }}</pre>
                </div>
                <div v-if="!log.oldValue && !log.newValue" class="no-change">
                  -
                </div>
              </div>
            </td>
            <td>{{ log.remark || '-' }}</td>
          </tr>
          <tr v-if="logs.length === 0">
            <td colspan="5" class="empty-state">
              尚無變更記錄
            </td>
          </tr>
        </tbody>
      </table>

      <ListPagination
        v-if="pagination.totalPages > 1"
        :page="pagination.page"
        :total-pages="pagination.totalPages"
        :total="pagination.totalItems"
        @change="handlePageChange"
      />
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { pointsEarningRulesService } from '@/api/modules/pointsEarningRules.js';
import { showError } from '@/utils/swal.js';

export default {
  name: 'PointsEarningRulesTimeRangeLogs',
  setup() {
    const router = useRouter();
    const route = useRoute();
    const { pageTitle } = usePageTitle('規則變更記錄');
    const logs = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const pagination = ref({
      page: 1,
      pageSize: 20,
      totalItems: 0,
      totalPages: 0,
      hasNext: false,
      hasPrev: false
    });

    const fetchLogs = async () => {
      loading.value = true;
      error.value = null;

      try {
        const params = {
          page: pagination.value.page,
          pageSize: pagination.value.pageSize
        };

        const response = await pointsEarningRulesService.getRuleLogs(route.params.id, params);

        if (response && response.success && response.data) {
          logs.value = response.data.items || [];
          pagination.value = response.data.pagination || pagination.value;
        } else {
          error.value = response?.message || '獲取變更記錄失敗';
        }
      } catch (err) {
        console.error('獲取變更記錄失敗:', err);
        error.value = err.message || '獲取變更記錄失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const handlePageChange = (page) => {
      pagination.value.page = page;
      fetchLogs();
    };

    const handleBack = () => {
      router.push('/client/points/earning-rules/time-range');
    };

    const getActionBadgeClass = (action) => {
      const classes = {
        create: 'badge badge-success',
        update: 'badge badge-info',
        enable: 'badge badge-success',
        disable: 'badge badge-warning',
        delete: 'badge badge-danger'
      };
      return classes[action] || 'badge badge-secondary';
    };

    const getActionText = (action) => {
      const texts = {
        create: '建立',
        update: '更新',
        enable: '啟用',
        disable: '停用',
        delete: '刪除'
      };
      return texts[action] || action;
    };

    const formatChangeValue = (value) => {
      if (!value) return '-';
      if (typeof value === 'string') {
        try {
          value = JSON.parse(value);
        } catch (e) {
          return value;
        }
      }
      return JSON.stringify(value, null, 2);
    };

    const getChangedByDisplay = (log) => {
      if (!log.changedBy) return '系統';
      // 優先顯示 name，如果沒有則顯示 username，都沒有則顯示 ID
      if (log.changedByName) return log.changedByName;
      if (log.changedByUsername) return log.changedByUsername;
      return log.changedBy;
    };

    const formatDateTime = (dateTime) => {
      if (!dateTime) return '-';
      const date = new Date(dateTime);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    };

    onMounted(() => {
      fetchLogs();
    });

    return {
      pageTitle,
      logs,
      loading,
      error,
      pagination,
      handlePageChange,
      handleBack,
      getActionBadgeClass,
      getActionText,
      formatChangeValue,
      getChangedByDisplay,
      formatDateTime,
      fetchLogs
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.change-details {
  max-width: 400px;
}

.old-value,
.new-value {
  margin-bottom: 8px;
  font-size: 12px;
}

.old-value {
  color: #dc3545;
}

.new-value {
  color: #28a745;
}

.old-value pre,
.new-value pre {
  margin: 4px 0 0 0;
  padding: 4px 8px;
  background: #f8f9fa;
  border-radius: 4px;
  font-size: 11px;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 150px;
  overflow-y: auto;
}

.no-change {
  color: #999;
  font-style: italic;
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #999;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
}

.page-info {
  color: #666;
  font-size: 14px;
}
</style>
